import { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import KPICard from '@/components/admin/KPICard';
import EmptyState from '@/components/admin/EmptyState';
import { adminApi } from '@/lib/api';
import { Users, CalendarCheck, MousePointerClick, Eye, Send, Hash, MapPin, PartyPopper, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from 'recharts';
import { ROUTES, STATUS_COLORS, STATUS_LABEL } from '@/lib/data';
import { format } from 'date-fns';

const NICE_DATE = (iso) => { try { return format(new Date(iso), 'dd/MM HH:mm'); } catch { return iso?.slice(0, 16); } };

export default function AdminDashboardPage() {
    const [stats, setStats] = useState(null);
    const [series, setSeries] = useState([]);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        Promise.all([
            adminApi.dashboardStats(),
            adminApi.dashboardTimeseries(7),
            adminApi.recentLeads(5),
        ]).then(([s, t, r]) => { setStats(s); setSeries(t); setRecent(r); }).finally(() => setLoading(false));
    }, []);
    const total = (stats?.page_views || 0) + (stats?.cta_clicks || 0) + (stats?.form_submits || 0);
    const hasAnyData = total > 0 || (stats?.total_leads || 0) > 0 || (stats?.total_reservations || 0) > 0;
    return (
        <AdminLayout title="Dashboard">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <KPICard icon={CalendarCheck} label="Reservas totales" value={stats?.total_reservations ?? 0} hint="Reservas online" accent="pink" />
                <KPICard icon={CalendarCheck} label="Reservas nuevas" value={stats?.new_reservations ?? 0} hint="Sin contactar" accent="yellow" />
                <KPICard icon={Users} label="Leads totales" value={stats?.total_leads ?? 0} hint="Formularios" accent="cyan" />
                <KPICard icon={Users} label="Leads nuevos" value={stats?.new_leads ?? 0} hint="Sin contactar" accent="blue" />
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                <KPICard icon={Eye} label="Páginas vistas" value={stats?.page_views ?? 0} accent="blue" />
                <KPICard icon={MousePointerClick} label="Clicks en CTAs" value={stats?.cta_clicks ?? 0} accent="pink" />
                <KPICard icon={Send} label="Forms enviados" value={stats?.form_submits ?? 0} accent="yellow" />
                <KPICard icon={Hash} label="Clicks sociales" value={stats?.social_clicks ?? 0} accent="pink" />
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-3">
                <div className="rounded-2xl bg-white border border-border p-4 lg:col-span-2 shadow-soft">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display font-semibold">Actividad últimos 7 días</h3>
                        <span className="text-xs text-muted-foreground">Vistas · Leads</span>
                    </div>
                    {loading ? (
                        <div className="h-64 animate-pulse rounded-xl bg-secondary mt-4" />
                    ) : !hasAnyData ? (
                        <div className="mt-4"><EmptyState title="Aún no hay datos" description="Las métricas aparecerán aquí cuando los visitantes interactúen con tu sitio." /></div>
                    ) : (
                        <div className="mt-4 h-64">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={series} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                                    <defs>
                                        <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(187 100% 50%)" stopOpacity={0.45} /><stop offset="100%" stopColor="hsl(187 100% 50%)" stopOpacity={0} /></linearGradient>
                                        <linearGradient id="gl" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="hsl(330 100% 52%)" stopOpacity={0.45} /><stop offset="100%" stopColor="hsl(330 100% 52%)" stopOpacity={0} /></linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(230 30% 90%)" />
                                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                    <Tooltip />
                                    <Legend />
                                    <Area type="monotone" dataKey="page_views" name="Vistas" stroke="hsl(187 100% 50%)" fill="url(#gv)" />
                                    <Area type="monotone" dataKey="leads" name="Leads" stroke="hsl(330 100% 52%)" fill="url(#gl)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
                <div className="rounded-2xl bg-white border border-border p-4 shadow-soft">
                    <div className="flex items-center justify-between">
                        <h3 className="font-display font-semibold">Leads recientes</h3>
                        <Link to={ROUTES.adminLeads} className="text-xs inline-flex items-center gap-1 text-[hsl(var(--kche-pink))]">Ver todos <ArrowRight className="size-3.5" /></Link>
                    </div>
                    {recent.length === 0 ? (
                        <div className="mt-4"><EmptyState title="Sin leads aún" description="Cuando alguien envíe un formulario lo verás aquí." /></div>
                    ) : (
                        <ul className="mt-3 divide-y divide-border">
                            {recent.map((l) => (
                                <li key={l.id} className="py-3 flex items-start gap-3">
                                    <span className="size-9 rounded-xl bg-secondary inline-flex items-center justify-center"><PartyPopper className="size-4 text-[hsl(var(--kche-pink))]" /></span>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2"><span className="font-medium text-sm truncate">{l.name}</span><span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_COLORS[l.status] || ''}`}>{STATUS_LABEL[l.status] || l.status}</span></div>
                                        <p className="text-xs text-muted-foreground truncate">{l.interest || l.source} · {NICE_DATE(l.created_at)}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </AdminLayout>
    );
}
